import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { rejectBody } from '../../utils/apiError';

const URL = '/api/gl-accounts';

// GET /api/gl-accounts?include_inactive= → { accounts: [...] }. Every row
// carries server-computed is_header/is_control/control_level — never derived
// client-side (GL-API-CONTRACT.md).
export const fetchGlAccounts = createAsyncThunk(
  'glAccounts/fetch',
  async (params, { rejectWithValue }) => {
    try {
      const res = await axios.get(URL, { params });
      return res.data?.data ?? res.data;
    } catch (error) {
      return rejectWithValue(rejectBody(error));
    }
  }
);

// POST /api/gl-accounts { parent_code, name_ar, name_en, code, type?, normal_balance? }
// → { account, warnings? }. type/normal_balance only sent from the Advanced
// disclosure; otherwise the server infers them from parent_code.
export const createGlAccount = createAsyncThunk(
  'glAccounts/create',
  async (data, { rejectWithValue }) => {
    try {
      const res = await axios.post(URL, data);
      return res.data?.data ?? res.data;
    } catch (error) {
      return rejectWithValue(rejectBody(error));
    }
  }
);

// PATCH /api/gl-accounts/:id { name_ar?, name_en?, is_active?, sort_order? }
export const updateGlAccount = createAsyncThunk(
  'glAccounts/update',
  async ({ id, ...patch }, { rejectWithValue }) => {
    try {
      const res = await axios.patch(`${URL}/${id}`, patch);
      return res.data?.data ?? res.data;
    } catch (error) {
      return rejectWithValue(rejectBody(error));
    }
  }
);

const glAccountsSlice = createSlice({
  name: 'glAccounts',
  initialState: {
    accounts: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchGlAccounts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGlAccounts.fulfilled, (state, action) => {
        state.accounts = Array.isArray(action.payload?.accounts) ? action.payload.accounts : [];
        state.loading = false;
      })
      .addCase(fetchGlAccounts.rejected, (state, action) => {
        state.loading = false;
        // Kept as the raw payload (not pre-stringified) so the page can run it
        // through errorText/fieldErrorMap for a translated, code-aware message.
        state.error = action.payload || action.error;
      });
  },
});

export default glAccountsSlice.reducer;
